import type { ApiClient, Schemas } from '../generated/laravel-typed-openapi';

/**
 * The signed-in user, read over REST — `GET /users/me`.
 *
 * Replaces the Lumen `getCurrentUserData` query. What it carries is identity and nothing else: the
 * name the account goes by, the address and card the subscription bills against, and the ids other
 * services know the user by. Everything the user can *choose* — the unit system, the date and
 * number formats, the time zone, both currencies — moved to `GET /users/settings` and is read
 * through `useSettings`.
 *
 * Two fields are renamed rather than dropped: the query's `card_last_four` and `card_brand` are
 * `pm_last_four` and `pm_type` here, which is what Cashier calls them.
 */

/** The signed-in user as the API returns it. */
export type CurrentUser = Schemas.UserResource;

/**
 * Reads the signed-in user.
 *
 * Throws `TypedStatusError` on any 4xx/5xx — the generated client's default — so an expired token
 * surfaces as a failed query rather than as an empty user, which would otherwise read as "signed in
 * as nobody" at every call site.
 */
export const getCurrentUser = async (apiClient: ApiClient): Promise<CurrentUser> => await apiClient.get('/users/me');

/**
 * The signed-in user's id as text.
 *
 * `UserResource.id` is a number, where the Lumen query typed it as a GraphQL `ID` and so handed it
 * back as a string. Almost everything it gets compared against is still a string — the ids in the
 * related-users list, the skipper a trip form holds, a comment's author — and `'5' === 5` is false,
 * so a bare swap would quietly stop recognising the user as themselves. Coercing in one place keeps
 * `===` meaning what it meant instead of asking every call site to remember.
 *
 * `undefined` while the user is unknown, which is what the query resolved to and what the callers
 * already branch on. Never the string `'undefined'`.
 */
export const toUserIdText = (user?: CurrentUser | null): string | undefined =>
  user?.id === undefined || user?.id === null ? undefined : String(user.id);
